/**
 * Testing Progress Sidebar Component
 *
 * Displays real-time testing progress metrics in the checklist runner page.
 * Shows steps completed, pass rate, issues found, and quick actions.
 * Supports manual/automated test type splitting.
 *
 * Module: 3.0 - System Operations (Unified Test Checklist Enhancement)
 * PRD: docs/prds/PRD-ISSUES-MANAGER.md v2.1
 */

import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, Bot, User } from "lucide-react";
import { getPassRateColor, getPassRateBackgroundColor } from "@/lib/constants/testing-constants";
import { cn } from "@/lib/utils";
import type { TestingChecklist } from "@shared/schema";

interface ItemCounts {
  total: number;
  automated: number;
  manual: number;
  passedAutomated: number;
  passedManual: number;
  failedAutomated: number;
  failedManual: number;
  pendingAutomated: number;
  pendingManual: number;
  issuesAutomated: number; // ISSUE-090: Count of issues linked to automated test items
  issuesManual: number; // ISSUE-090: Count of issues linked to manual test items
}

interface TestingProgressSidebarProps {
  checklist: TestingChecklist & {
    sprintNumber?: string;
    itemCounts?: ItemCounts;
  };
  stepsCompleted: number;
  totalSteps: number;
  issuesFound: number;
  passRate: number;
}

export function TestingProgressSidebar({
  checklist,
  stepsCompleted,
  totalSteps,
  issuesFound,
  passRate,
}: TestingProgressSidebarProps) {
  const progressPercentage = totalSteps > 0 ? (stepsCompleted / totalSteps) * 100 : 0;
  const itemCounts = checklist.itemCounts;

  // Calculate completed counts by type
  const completedAutomated = itemCounts
    ? itemCounts.passedAutomated + itemCounts.failedAutomated
    : 0;
  const completedManual = itemCounts
    ? itemCounts.passedManual + itemCounts.failedManual
    : 0;

  // Calculate pass rates by type
  const automatedPassRate = completedAutomated > 0
    ? (itemCounts!.passedAutomated / completedAutomated) * 100
    : 0;
  const manualPassRate = completedManual > 0
    ? (itemCounts!.passedManual / completedManual) * 100
    : 0;

  // ISSUE-090: Display actual issue counts by type (not test failure counts)
  const automatedIssues = itemCounts?.issuesAutomated || 0;
  const manualIssues = itemCounts?.issuesManual || 0;

  return (
    <Card className="sticky top-4 h-fit w-full max-w-[320px]">
      <CardHeader>
        <CardTitle className="text-lg">Testing Progress</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Steps Completed - Overall */}
        <div>
          <div className="flex justify-between text-sm mb-2">
            <span className="text-muted-foreground">Steps Completed</span>
            <span className="font-semibold">
              {stepsCompleted} / {totalSteps}
            </span>
          </div>
          <Progress value={progressPercentage} className="h-2" />
          <p className="text-xs text-muted-foreground mt-1">
            {progressPercentage.toFixed(1)}% complete
          </p>

          {/* Steps Completed - By Type */}
          {itemCounts && (
            <div className="mt-3 space-y-1.5 text-xs">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Bot className="h-3 w-3" />
                  <span>Automated</span>
                </div>
                <span className="font-medium">
                  {completedAutomated} / {itemCounts.automated}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <User className="h-3 w-3" />
                  <span>Manual</span>
                </div>
                <span className="font-medium">
                  {completedManual} / {itemCounts.manual}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Pass Rate - Overall */}
        <div>
          <div className="flex justify-between text-sm mb-2">
            <span className="text-muted-foreground">Pass Rate</span>
            <span className={cn("font-semibold", getPassRateColor(passRate))}>
              {passRate.toFixed(1)}%
            </span>
          </div>
          <div className="relative h-2 w-full overflow-hidden rounded-full bg-secondary">
            <div
              className={cn("h-full transition-all", getPassRateBackgroundColor(passRate))}
              style={{ width: `${passRate}%` }}
            />
          </div>

          {/* Pass Rate - By Type */}
          {itemCounts && (completedAutomated > 0 || completedManual > 0) && (
            <div className="mt-3 space-y-1.5 text-xs">
              {completedAutomated > 0 && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Bot className="h-3 w-3" />
                    <span>Automated</span>
                  </div>
                  <span className={cn("font-medium", getPassRateColor(automatedPassRate))}>
                    {automatedPassRate.toFixed(1)}%
                  </span>
                </div>
              )}
              {completedManual > 0 && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <User className="h-3 w-3" />
                    <span>Manual</span>
                  </div>
                  <span className={cn("font-medium", getPassRateColor(manualPassRate))}>
                    {manualPassRate.toFixed(1)}%
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Issues Found */}
        <div>
          <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
            Issues Found
          </h4>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Issues</span>
              <Badge variant="outline">{issuesFound}</Badge>
            </div>

            {/* Issues - By Type */}
            {itemCounts && (automatedIssues > 0 || manualIssues > 0) && (
              <div className="mt-2 space-y-1 text-xs">
                {automatedIssues > 0 && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <Bot className="h-3 w-3" />
                      <span>Automated</span>
                    </div>
                    <Badge variant="outline" className="text-xs h-5">
                      {automatedIssues}
                    </Badge>
                  </div>
                )}
                {manualIssues > 0 && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <User className="h-3 w-3" />
                      <span>Manual</span>
                    </div>
                    <Badge variant="outline" className="text-xs h-5">
                      {manualIssues}
                    </Badge>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="space-y-2">
          <Button variant="outline" className="w-full" size="sm" asChild>
            <Link to={`/issues?sprint=${checklist.sprintNumber || checklist.sprintId}`}>
              View Issues for {checklist.sprintNumber || checklist.sprintId}
            </Link>
          </Button>
        </div>

        {/* Checklist Info */}
        <div className="pt-4 border-t space-y-2">
          <h4 className="text-sm font-semibold mb-2">Checklist Info</h4>
          <div className="space-y-1 text-sm text-muted-foreground">
            <div className="flex justify-between">
              <span>Sprint:</span>
              <span className="font-medium text-foreground">{checklist.sprintId}</span>
            </div>
            <div className="flex justify-between">
              <span>Type:</span>
              <span className="font-medium text-foreground capitalize">
                {checklist.checklistType}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Created:</span>
              <span className="font-medium text-foreground">
                {new Date(checklist.createdAt).toLocaleDateString("en-AU")}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
